/**
 * The console's API base URL is never compiled in. It is read once at boot
 * from /runtime-config.json, a file the deployment container writes (or, in
 * dev, the static default checked into public/runtime-config.json). This is
 * what lets a rename or a host move be a runtime edit instead of a rebuild —
 * see docs/design-handoff/README.md and Andrew's resolution #2 in the task
 * brief.
 */
export interface RuntimeConfig {
  /** Origin (or empty string for same-origin) the ops client prefixes every request with. */
  apiBase: string
}

const SAME_ORIGIN_DEFAULT: RuntimeConfig = { apiBase: '' }

let cached: RuntimeConfig | null = null
let inflight: Promise<RuntimeConfig> | null = null

function normalize(input: unknown): RuntimeConfig {
  if (input && typeof input === 'object' && typeof (input as { apiBase?: unknown }).apiBase === 'string') {
    return { apiBase: (input as { apiBase: string }).apiBase }
  }
  return SAME_ORIGIN_DEFAULT
}

/**
 * Fetches and caches /runtime-config.json. Falls back to same-origin ("")
 * if the file is missing, unreachable or malformed — a fresh dev checkout
 * with no container-written config should still boot.
 */
export async function loadRuntimeConfig(fetchImpl: typeof fetch = fetch): Promise<RuntimeConfig> {
  if (cached) return cached
  if (!inflight) {
    inflight = fetchImpl('/runtime-config.json', { cache: 'no-store' })
      .then((res) => (res.ok ? res.json() : SAME_ORIGIN_DEFAULT))
      .catch(() => SAME_ORIGIN_DEFAULT)
      .then((body) => {
        cached = normalize(body)
        inflight = null
        return cached
      })
  }
  return inflight
}

/** Test-only: clears the cache so each test can control the next fetch. */
export function resetRuntimeConfigForTests(): void {
  cached = null
  inflight = null
}
